import { useState, useEffect } from "react";

const KLOOK_AID = import.meta.env.VITE_KLOOK_AID || "116395";

const CATEGORIES = [
  { value: "sightseeing", label: "Sightseeing", icon: "🏛️" },
  { value: "dining", label: "Dining", icon: "🍽️" },
  { value: "transport", label: "Transport", icon: "🚗" },
  { value: "accommodation", label: "Accommodation", icon: "🏨" },
  { value: "adventure", label: "Adventure", icon: "🏔️" },
  { value: "shopping", label: "Shopping", icon: "🛍️" },
  { value: "leisure", label: "Leisure", icon: "🌳" },
];

const TIME_SLOTS = [
  "6:00 AM", "6:30 AM", "7:00 AM", "7:30 AM", "8:00 AM", "8:30 AM",
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM",
  "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM", "8:30 PM",
  "9:00 PM", "9:30 PM", "10:00 PM", "10:30 PM", "11:00 PM"
];

const getKlookSearchUrl = (query, destination) => {
  const params = new URLSearchParams({
    search_landing: 'true',
    query: `Explore ${query}${destination ? ` ${destination}` : ''}`.trim(),
    aid: KLOOK_AID,
    utm_medium: 'affiliate-alwayson',
    utm_source: 'non-network',
    utm_campaign: KLOOK_AID,
    utm_term: '',
  });
  return `https://www.klook.com/search/result/?${params.toString()}`;
};

const getKlookActivityUrl = (activityId) => {
  return `https://www.klook.com/activity/${activityId}/?aid=${KLOOK_AID}`;
};

const ActivityEditModal = ({
  isOpen,
  onClose,
  onSave,
  activity = null,
  dayNumber,
  destination,
  currency = "INR"
}) => {
  const isEditing = !!activity;

  const [formData, setFormData] = useState({
    time: "9:00 AM",
    title: "",
    description: "",
    duration: "2 hours",
    estimatedCost: "",
    category: "sightseeing",
    tips: "",
    imageKeywords: [],
  });

  const [errors, setErrors] = useState({});
  const [showKlookSuggestions, setShowKlookSuggestions] = useState(false);

  const klookSuggestions = {
    sightseeing: [
      "City Tour", "Museum Visit", "Historical Monument", "Temple Visit",
      "Walking Tour", "Hop-on Hop-off Bus", "Observation Deck"
    ],
    adventure: [
      "Snorkeling", "Scuba Diving", "Parasailing", "Jet Ski",
      "Bungee Jumping", "Zip Line", "ATV Ride", "White Water Rafting"
    ],
    dining: [
      "Food Tour", "Cooking Class", "Street Food Tour", "Fine Dining"
    ],
    leisure: [
      "Spa & Massage", "Beach Club", "Theme Park", "Water Park",
      "Sunset Cruise", "Shopping Tour"
    ]
  };

  useEffect(() => {
    if (activity) {
      setFormData({
        time: activity.time || "9:00 AM",
        title: activity.title || "",
        description: activity.description || "",
        duration: activity.duration || "2 hours",
        estimatedCost: activity.estimatedCost?.toString().replace(/[^\d]/g, '') || "",
        category: activity.category || "sightseeing",
        tips: activity.tips || "",
        imageKeywords: activity.imageKeywords || [],
      });
    } else {
      setFormData({
        time: "9:00 AM",
        title: "",
        description: "",
        duration: "2 hours",
        estimatedCost: "",
        category: "sightseeing",
        tips: "",
        imageKeywords: [],
      });
    }
    setErrors({});
  }, [activity, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (!formData.time) newErrors.time = "Time is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const activityData = {
      ...formData,
      estimatedCost: formData.estimatedCost
        ? `${currency} ${parseInt(formData.estimatedCost).toLocaleString()}`
        : "Free",
      imageKeywords: formData.title
        ? [formData.title, destination, formData.category].filter(Boolean)
        : [destination],
    };

    onSave(activityData);
    onClose();
  };

  const handleSuggestionClick = (suggestion) => {
    handleChange("title", `${suggestion} in ${destination}`);
    handleChange("description", `Experience ${suggestion.toLowerCase()} in ${destination}. A must-do activity for visitors.`);
    setShowKlookSuggestions(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl transform transition-all">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {isEditing ? "Edit Activity" : "Add New Activity"}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Day {dayNumber} - {destination}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🎫</span>
                  <div>
                    <p className="font-semibold text-gray-900">Find activities on Klook</p>
                    <p className="text-sm text-gray-600">Browse and book tickets at best prices</p>
                  </div>
                </div>
                <a
                  href={getKlookSearchUrl(formData.title || "things to do", destination)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Search Klook
                </a>
              </div>

              <div className="mt-3 pt-3 border-t border-orange-100">
                <p className="text-xs text-gray-500 mb-2">Quick suggestions:</p>
                <div className="flex flex-wrap gap-2">
                  {(klookSuggestions[formData.category] || klookSuggestions.sightseeing).slice(0, 5).map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="px-3 py-1 text-xs bg-white border border-orange-200 rounded-full hover:bg-orange-50 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.time}
                    onChange={(e) => handleChange("time", e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                      errors.time ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    {TIME_SLOTS.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                  {errors.time && <p className="text-red-500 text-xs mt-1">{errors.time}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="e.g., Visit Eiffel Tower"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    errors.title ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Describe what you'll do during this activity..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none ${
                    errors.description ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => handleChange("duration", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="30 mins">30 mins</option>
                    <option value="1 hour">1 hour</option>
                    <option value="1.5 hours">1.5 hours</option>
                    <option value="2 hours">2 hours</option>
                    <option value="2.5 hours">2.5 hours</option>
                    <option value="3 hours">3 hours</option>
                    <option value="4 hours">4 hours</option>
                    <option value="Half day">Half day</option>
                    <option value="Full day">Full day</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Cost ({currency})
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {currency}
                    </span>
                    <input
                      type="number"
                      value={formData.estimatedCost}
                      onChange={(e) => handleChange("estimatedCost", e.target.value)}
                      placeholder="0 for free"
                      className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave empty or 0 for free activities</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tips (Optional)
                </label>
                <input
                  type="text"
                  value={formData.tips}
                  onChange={(e) => handleChange("tips", e.target.value)}
                  placeholder="e.g., Book tickets in advance to avoid queues"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
            >
              {isEditing ? "Save Changes" : "Add Activity"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityEditModal;
